// src/pages/InvocationsPage/InvocationsPage.tsx
import React, { useRef } from 'react';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import InvocationCard from '../../components/InvocationCard/InvocationCard';
import Section from '../../components/Section/Section';
import Navigation from '../../components/Navigation/Navigation';
import { PageData } from '../../data/types';
import invocationsSoucisData from '../../data/invocations-soucis.json';
import './InvocationsPage.css';
import SEO from '../../components/SEO/SEO';

interface InvocationsPageProps {
  pageData?: PageData;
}

const InvocationsPage: React.FC<InvocationsPageProps> = ({ pageData = invocationsSoucisData as PageData }) => {
  const introRef = useRef<HTMLDivElement>(null);
  const patiencePriereRef = useRef<HTMLDivElement>(null);

  // Scroll vers le haut quand la page se charge
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!pageData) {
    return (
      <>
        <Navigation />
        <div className="error-container">
          <h2>Erreur</h2>
          <p>Données non disponibles</p>
        </div>
      </>
    );
  }

    return (
    <div className="invocations-page">
      <Navigation />
      
      <SEO 
        title="Invocations contre les soucis et la tristesse"
        description="Collection d'invocations prophétiques authentiques enseignées par Cheykh Souleymâne El-Rouheylî pour apaiser le cœur et chasser les soucis. Invocations avec audio et traduction française."
        url="https://mahi-bayan.netlify.app/invocations-soucis"
      />
      
      {/* Wrapper avec padding-top pour compenser la navigation fixe */}
      <div className="page-content">
        <Header 
          mainTitle={pageData.header.mainTitle}
          subtitle={pageData.header.subtitle}
        />

        <main className="content">
          <div className="bismillah">{pageData.bismillah}</div>

          {pageData.intro && (
            <div ref={introRef} className="invocation special-section">
              <h3>{pageData.intro.author} :</h3>
              <div className="translation">{pageData.intro.content}</div>
            </div>
          )}

          <div className="sections-container">
            {pageData.sections.map((section, index) => {
              const isFirstHadith = index === 0;
              const isPatiencePriere = index === 2;
              const isBukhariHadith = index === 3;
              
              return (
                <div 
                  key={index} 
                  ref={isPatiencePriere ? patiencePriereRef : null}
                >
                  <Section 
                    {...section} 
                    showCombinedDownload={isFirstHadith}
                    introRef={isFirstHadith ? introRef : undefined}
                    showPreviousSectionDownload={isBukhariHadith}
                    previousSectionRef={isBukhariHadith ? patiencePriereRef : undefined}
                  />
                </div>
              );
            })}
          </div>

          <div className="invocations-container">
            {pageData.invocations.map((invocation) => (
              <InvocationCard key={invocation.id} {...invocation} />
            ))}
          </div>

          {pageData.conclusion && (
            <Section
              type="special"
              title={pageData.conclusion.title}
              subtitle={pageData.conclusion.subtitle}
              arabicText={pageData.conclusion.arabicText || ""}
              translation={pageData.conclusion.translation}
              source={pageData.conclusion.source}
              borderColor="#7c3aed"
              showIndividualDownload={true}
            />
          )}
        </main>

        <Footer texts={pageData.footer} />
      </div>
    </div>
  );
};

export default InvocationsPage;
